import { __awaiter } from "tslib";
export function alphanumericCompare(a, b) {
    const aParts = a.match(/[^\d]+|\d+/g);
    const bParts = b.match(/[^\d]+|\d+/g);
    // Empty strings
    if (!aParts) {
        return -1;
    }
    if (!bParts) {
        return 1;
    }
    for (let partIndex = 0; partIndex < aParts.length && partIndex < bParts.length; partIndex++) {
        const partComparison = compareAlphaOrNumeric(aParts[partIndex], bParts[partIndex]);
        if (partComparison !== 0) {
            return partComparison;
        }
    }
    return aParts.length - bParts.length;
}
function compareAlphaOrNumeric(a, b) {
    const aNumber = parseInt(a, 10);
    const bNumber = parseInt(b, 10);
    if (isNaN(aNumber) || isNaN(bNumber)) {
        return a.toLowerCase().localeCompare(b.toLowerCase());
    }
    return aNumber - bNumber;
}
/**
 * Returns an array containing an (inclusive) range between the given numbers.
 *
 * The increment between items is always 1.
 *
 * @example
 * range(1, 3)
 * //=> [1, 2, 3]
 */
export function range(first, last) {
    return new Array(last - first + 1).fill(undefined).map((_, idx) => first + idx);
}
/**
 * Collects the first item that is not undefined
 * Think of this like map().find() without having to iterate twice
 */
export function collectFirst(array, collector) {
    for (const a of array) {
        const result = collector(a);
        if (result) {
            return result;
        }
    }
    return undefined;
}
/**
 * Applies a map function, that can be undefined.
 * And filters any cases where it would return undefined
 *
 * Think of this like map().filter(x => !x) without having to iterate twice
 */
export function collect(array, collector) {
    return array.reduce((acc, value) => {
        const res = collector(value);
        return res ? acc.concat([res]) : acc;
    }, []);
}
/**
 * Filters the array based on an async predicate
 */
export function filterAsync(array, predicate) {
    return __awaiter(this, void 0, void 0, function* () {
        const results = yield Promise.all(array.map(predicate));
        return array.filter((_, index) => results[index]);
    });
}
/**
 * Returns an array with arrays of the given size.
 *
 * @link https://ourcodeworld.com/articles/read/278/how-to-split-an-array-into-chunks-of-the-same-size-easily-in-javascript
 * @param arr Array to split
 * @param chunkSize Size of every group
 */
export function chunkArray(arr, chunkSize) {
    const results = [];
    // Per the link in codedoc, a while is up to 8x more efficient than a for loop, a map, etc.
    while (arr.length) {
        results.push(arr.splice(0, chunkSize));
    }
    return results;
}
/**
 * Use this when calling filter on an array to remove the `| undefined` variant
 * If you did a map previously you could consider calling `collect` instead.
 */
export function defined(x) {
    return x !== undefined;
}
/**
 * Returns array of unique values included in all arrays.
 */
export function intersection(...arrays) {
    return unique(arrays.reduce((acc, array) => acc.filter(value => array.includes(value))));
}
/**
 * Returns array of unique objects included in all arrays using IDs to compare.
 */
export function intersectionById(...arrays) {
    return uniqueById(arrays.reduce((acc, array) => acc.filter(value => array.some(({ id }) => id === value.id))));
}
/**
 * Returns duplicate-free version of array.
 */
export function unique(array) {
    return Array.from(new Set(array));
}
/**
 * Returns duplicate-free version of array using IDs to compare.
 */
export function uniqueById(array) {
    return array.filter((value, index) => index === array.findIndex(({ id }) => id === value.id));
}
/**
 * Fast sort which ignores accents and case, i.e. a ≠ b, a = á, a = A.
 */
export const sortAlphabetically = new Intl.Collator(undefined, { sensitivity: 'base' }).compare;
/**
 * Applies {@link sortAlphabetically} to props until order is determined or props are exhausted.
 */
export function sortAlphabeticallyByProps(...props) {
    return (a, b) => {
        for (const prop of props) {
            const aValue = a[prop];
            const bValue = b[prop];
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const result = sortAlphabetically(aValue, bValue);
                if (result) {
                    return result;
                }
            }
        }
        return 0;
    };
}
/**
 * Returns distinct set of values from a given array of primitive type.
 */
export function distinct(array) {
    return Array.from(new Set(array));
}
/**
 * Returns the sum of all values from a given array of primitive number type.
 */
export function sum(array) {
    if (!array.length) {
        return 0;
    }
    return array.reduce((a, b) => a + b);
}
export function isStringArray(value) {
    return Array.isArray(value) && value.length > 0 && value.every(x => typeof x === 'string');
}
